import { Box, SvgIcon, Tooltip, type SvgIconProps } from "@mui/material";
import type { IntegrationId } from "../types";

interface IntegrationIconProps extends Omit<SvgIconProps, "color"> {
    size?: number;
}

export const JiraIcon = ({ size = 14, sx, ...rest }: IntegrationIconProps) => (
    <SvgIcon viewBox="0 0 24 24" sx={{ width: size, height: size, fontSize: size, ...sx }} {...rest}>
        <path d="M11.5 2.5L3 11l4 4 4.5-4.5L7 6l4.5-4z" fill="#2684FF" fillOpacity={0.95} />
        <path d="M12.5 21.5L21 13l-4-4-4.5 4.5L17 18l-4.5 4z" fill="#2684FF" fillOpacity={0.6} />
    </SvgIcon>
);

export const AzureIcon = ({ size = 14, sx, ...rest }: IntegrationIconProps) => (
    <SvgIcon viewBox="0 0 24 24" sx={{ width: size, height: size, fontSize: size, ...sx }} {...rest}>
        <path d="M5 19l5.5-9 4 4.5L19 19H5z" fill="#0078D4" fillOpacity={0.95} />
        <path d="M10.5 10l2.5-5 6 14-4.5-3.5L10.5 10z" fill="#0078D4" fillOpacity={0.55} />
    </SvgIcon>
);

export const GitIcon = ({ size = 14, sx, ...rest }: IntegrationIconProps) => (
    <SvgIcon
        viewBox="0 0 24 24"
        sx={{ width: size, height: size, fontSize: size, ...sx }}
        {...rest}
    >
        <g fill="none" stroke="#F05033" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="6" cy="6" r="2.4" fill="#F05033" />
            <circle cx="6" cy="18" r="2.4" fill="#F05033" />
            <circle cx="18" cy="9" r="2.4" fill="#F05033" />
            <path d="M6 8.4v7.2" />
            <path d="M6 12c0-3 2-5 5-5h3.5" />
        </g>
    </SvgIcon>
);

const INTEGRATION_META: Record<IntegrationId, { label: string; Icon: typeof JiraIcon }> = {
    jira: { label: "Linked to Jira", Icon: JiraIcon },
    azure: { label: "Linked to Azure DevOps", Icon: AzureIcon },
    git: { label: "Connected repository", Icon: GitIcon }
};

interface IntegrationIconsProps {
    ids: IntegrationId[];
    size?: number;
    gap?: number;
}

const IntegrationIcons = ({ ids, size = 14, gap = 0.75 }: IntegrationIconsProps) => {
    if (!ids.length) return null;
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap }}>
            {ids.map((id) => {
                const meta = INTEGRATION_META[id];
                if (!meta) return null;
                const { Icon, label } = meta;
                return (
                    <Tooltip key={id} title={label} arrow>
                        <Box component="span" sx={{ display: "inline-flex", lineHeight: 0 }}>
                            <Icon size={size} />
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
};

export default IntegrationIcons;
